import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, HelpCircle, AlertTriangle } from 'lucide-react';

const MAILTO_BODY = encodeURIComponent(
  `Mô tả vấn đề:


URL hiện tại:

--- LƯU Ý ---
Vui lòng KHÔNG dán/đính kèm nội dung tài liệu nhạy cảm vào email.
Chỉ gửi mã giao dịch / tên file / mô tả lỗi nếu cần.
Nội dung dịch xử lý trong app.`
);
const MAILTO_LINK = `mailto:support@dichtailieu.vn?subject=${encodeURIComponent('[Beta Support] ')}&body=${MAILTO_BODY}`;

const FAQ_ITEMS: Array<{ q: string; a: React.ReactNode }> = [
  {
    q: 'Có popup xin quyền chọn thư mục hiện lên, tôi nên bấm cho phép hay hủy?',
    a: (
      <>
        Bấm <strong>Cho phép</strong>. App chỉ truy cập đúng thư mục bạn chọn để lưu bản dịch — không tự ý đọc thư mục khác. Bạn có thể đổi thư mục bất cứ lúc nào.
      </>
    ),
  },
  {
    q: 'File Word/PowerPoint của tôi tải lên bị lỗi, có phải do file quá nặng không?',
    a: (
      <>
        Nếu báo lỗi quá nặng, bạn chia file thành nhiều file con theo chương/phần rồi upload từng file. Cũng có thể save lại ở định dạng <code className="bg-slate-100 px-1.5 py-0.5 rounded text-xs">.docx</code> / <code className="bg-slate-100 px-1.5 py-0.5 rounded text-xs">.pptx</code> mới nhất nếu file đang là <code className="bg-slate-100 px-1.5 py-0.5 rounded text-xs">.doc</code> / <code className="bg-slate-100 px-1.5 py-0.5 rounded text-xs">.ppt</code> cũ. Vẫn lỗi → gửi tên file qua <a href={MAILTO_LINK} className="text-emerald-600 hover:underline">support@dichtailieu.vn</a>.
      </>
    ),
  },
  {
    q: 'Tôi mở mã QR lâu quá mới thanh toán, giờ có cần tạo mã mới không?',
    a: (
      <>
        Bấm <strong>Tạo lại mã QR</strong> để có mã mới và tiếp tục. Nếu bạn đã chuyển khoản theo mã cũ, <strong>tiền vẫn được match</strong> qua mã giao dịch trong nội dung chuyển khoản — không mất tiền.
      </>
    ),
  },
  {
    q: 'Có nên gửi tài liệu gốc qua email support không?',
    a: (
      <>
        Không nên gửi tài liệu gốc qua email. Khi cần hỗ trợ, bạn chỉ gửi: tên file, ảnh chụp màn hình lỗi, mã giao dịch, hoặc mô tả vấn đề. Nếu bắt buộc cần xem nội dung, support sẽ hướng dẫn cách che/xóa phần nhạy cảm trước.
      </>
    ),
  },
  {
    q: 'Máy tôi sập hoặc tab đóng khi đang dịch dở chừng, có mất tiền credit không?',
    a: (
      <>
        Bạn không mất gì. App lưu progress sau mỗi đoạn dịch xong. Mở lại app trong 48 giờ, hệ thống tự khôi phục bản dịch và <strong>không trừ thêm credit</strong> cho phần đã làm. <em>(Lưu ý: nếu dùng Chế độ Ẩn Danh thì không có khôi phục — cần dịch lại từ đầu.)</em>
      </>
    ),
  },
];

const HelpPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-white">
      <nav className="px-6 py-4 border-b border-slate-100 bg-white sticky top-0 z-10">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-slate-600 hover:text-emerald-600 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm font-medium">Về trang chủ</span>
          </Link>
          <div className="flex items-center gap-2">
            <HelpCircle className="w-5 h-5 text-emerald-600" />
            <span className="font-black text-slate-800">Trợ giúp</span>
          </div>
        </div>
      </nav>

      <article className="max-w-3xl mx-auto px-6 py-12">
        <h1 className="text-3xl font-black text-slate-900 mb-2">Trợ giúp</h1>
        <p className="text-slate-500 text-sm mb-8">Câu hỏi thường gặp và cách liên hệ hỗ trợ.</p>

        {/* FAQ */}
        <section className="mb-12">
          <h2 className="text-xl font-bold text-slate-800 mb-4">Câu hỏi thường gặp</h2>
          <div className="space-y-4">
            {FAQ_ITEMS.map((item, i) => (
              <details key={i} className="group bg-white rounded-xl border border-slate-200 overflow-hidden">
                <summary className="px-5 py-4 cursor-pointer font-bold text-slate-800 hover:bg-slate-50 flex items-center justify-between gap-4">
                  <span>{item.q}</span>
                  <span className="text-emerald-500 group-open:rotate-180 transition-transform shrink-0">▼</span>
                </summary>
                <div className="px-5 pb-4 text-slate-600 leading-relaxed">{item.a}</div>
              </details>
            ))}
          </div>
        </section>

        {/* Contact */}
        <section className="mb-12">
          <h2 className="text-xl font-bold text-slate-800 mb-4">Vẫn cần hỗ trợ?</h2>
          <p className="text-slate-700 mb-2">
            Email:{' '}
            <a href={MAILTO_LINK} className="text-emerald-600 font-semibold hover:underline">
              support@dichtailieu.vn
            </a>
          </p>
          <p className="text-sm text-slate-500 mb-4">
            Thường phản hồi trong ngày (8h-22h hằng ngày). Vấn đề thanh toán được ưu tiên kiểm tra.
          </p>

          <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="font-semibold text-amber-900 flex items-center gap-2 mb-1">
              <AlertTriangle className="w-4 h-4" />
              Bảo mật khi liên hệ
            </p>
            <p className="text-sm text-amber-800 leading-relaxed">
              Vui lòng <strong>KHÔNG</strong> gửi nội dung tài liệu nhạy cảm (báo cáo, hợp đồng, audit report...) qua email. Chỉ gửi: mã giao dịch, tên file, hoặc mô tả lỗi. Nội dung dịch luôn xử lý trong app — dùng <Link to="/privacy" className="text-amber-900 underline">chế độ Ẩn Danh</Link> nếu nhạy cảm.
            </p>
          </div>
        </section>

        {/* Legal links */}
        <section>
          <p className="text-sm text-slate-500">
            Vấn đề pháp lý:{' '}
            <Link to="/privacy" className="text-emerald-600 hover:underline">Chính sách bảo mật</Link>
            {' · '}
            <Link to="/tos" className="text-emerald-600 hover:underline">Điều khoản sử dụng</Link>
            {' · '}
            <Link to="/refund" className="text-emerald-600 hover:underline">Chính sách hoàn tiền</Link>
          </p>
        </section>
      </article>
    </div>
  );
};

export default HelpPage;
